// Staff queries and mutations (spec §5.9). Pattern for every area: keys + useXxxQuery/useXxxMutation,
// each mutation invalidates what it changes. Components never call the API.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client, unwrap } from "@/api";
import type { StaffRole } from "@/shell/roles";

export const staffKeys = {
  all: ["staff"] as const,
  list: (limit: number, offset: number) => ["staff", "list", limit, offset] as const,
};

export function useStaffQuery(limit: number, offset: number) {
  return useQuery({
    queryKey: staffKeys.list(limit, offset),
    queryFn: () => unwrap(client.GET("/api/v1/admin/staff", { params: { query: { limit, offset } } })),
  });
}

export type StaffCreate = { email: string; display_name: string; roles: StaffRole[] };
export type StaffPatch = { roles?: StaffRole[]; status?: "ACTIVE" | "BLOCKED"; display_name?: string };

function useInvalidateStaff() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: staffKeys.all });
}

export function useCreateStaffMutation() {
  const invalidate = useInvalidateStaff();
  return useMutation({
    mutationFn: (body: StaffCreate) => unwrap(client.POST("/api/v1/admin/staff", { body })),
    onSuccess: invalidate,
  });
}

export function useUpdateStaffMutation() {
  const invalidate = useInvalidateStaff();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: StaffPatch }) =>
      unwrap(client.PATCH("/api/v1/admin/staff/{id}", { params: { path: { id } }, body: patch })),
    onSuccess: invalidate,
  });
}

export function useResetStaffTotpMutation() {
  const invalidate = useInvalidateStaff();
  return useMutation({
    mutationFn: (id: string) => unwrap(client.POST("/api/v1/admin/staff/{id}/2fa/reset", { params: { path: { id } } })),
    onSuccess: invalidate,
  });
}

export function useStaffPasswordResetMutation() {
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(client.POST("/api/v1/admin/staff/{id}/password-reset", { params: { path: { id } } })),
  });
}
